/** Admin API client. Operator-admin always points at the same dev API.
 *
 * Auth model: every `/v1/admin/*` route now accepts a session bearer
 * token from an `is_operator=true` account (dual-auth — see backend
 * `deps.require_admin_session_or_operator_auth`). We persist the access
 * token in localStorage so a tab refresh doesn't kick you back to login.
 * On any 401 from the API we clear the token and dispatch an
 * `admin-auth-expired` event the shell listens to in order to swap
 * back to the login screen.
 */

const API_BASE: string =
  (import.meta as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE ??
  "https://api.dovach.local:8080";

const TOKEN_KEY = "dovach.admin.access_token";

export const authStore = {
  get(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set(token: string): void {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* private-browsing or quota — silently fall through */
    }
  },
  clear(): void {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
  },
};

export class ApiError extends Error {
  constructor(public status: number, public detail: string) {
    super(`API ${status}: ${detail}`);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  let payload: BodyInit | undefined;
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  const token = authStore.get();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const resp = await fetch(API_BASE + path, {
    method,
    headers,
    body: payload,
    credentials: "omit",
  });
  if (resp.status === 401) {
    // Session is dead. Tell the shell to swap to the login screen and
    // let the caller surface whatever message the API sent.
    authStore.clear();
    window.dispatchEvent(new CustomEvent("admin-auth-expired"));
  }
  if (!resp.ok) {
    let detail = `http_${resp.status}`;
    try {
      const j = await resp.json();
      if (j && typeof j.detail === "string") detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new ApiError(resp.status, detail);
  }
  if (resp.status === 204) return undefined as T;
  return resp.json() as Promise<T>;
}

// ─────────────────────────────────────────────────────────────── auth

export interface LoginResponse {
  tokens?: { access_token: string; refresh_token: string };
  needs_2fa?: boolean;
  partial_token?: string;
}

export interface MeResponse {
  account_id: string;
  email: string;
  display_name: string;
  has_2fa: boolean;
}

export interface HealthOverview {
  api: boolean;
  oracle: boolean;
  object_storage: boolean;
}

export interface RecentCert {
  account_id: string;
  email: string;
  office_pk: string;
  fingerprint: string;
}

export interface AlertItem {
  alert_id: string;
  account_id: string | null;
  kind: string;
  severity: string;
  body_he: string;
  created_at: string;
  resolved_at: string | null;
}

export interface PendingKeypair {
  account_id: string;
  email: string;
  display_name: string;
  office_pk: string;
  enrolled_at: string;
}

export interface ExpiringCert {
  account_id: string;
  email: string;
  office_pk: string;
  not_after: string;
  days_until_expiry: number;
}

export interface AuditSearchRow {
  seq: number;
  ts: string;
  account_id: string | null;
  actor: string;
  action: string;
  resource_id: string | null;
  prev_hash: string;
  this_hash: string;
}

export const api = {
  // ── auth ────────────────────────────────────────────────────────
  login: (email: string, password: string) =>
    request<LoginResponse>("POST", "/v1/auth/login", { email, password }),
  verify2fa: (partial_token: string, code: string) =>
    request<{ access_token: string; refresh_token: string }>(
      "POST", "/v1/auth/2fa/verify", { partial_token, code },
    ),
  me: () => request<MeResponse>("GET", "/v1/auth/me"),
  logout: () =>
    request<void>("POST", "/v1/auth/logout", { refresh_token: "" }).catch(() => {}),

  // ── admin surfaces ──────────────────────────────────────────────
  health: () => request<HealthOverview>("GET", "/v1/admin/health"),
  recentCerts: () =>
    request<{ certs: RecentCert[] }>("GET", "/v1/admin/certs/recent"),

  alerts: (only_open: boolean) =>
    request<{ alerts: AlertItem[] }>(
      "GET",
      `/v1/admin/alerts?only_open=${only_open ? "true" : "false"}`,
    ),
  alertResolve: (alert_id: string) =>
    request<{ ok: boolean }>(
      "POST",
      `/v1/admin/alerts/${alert_id}/resolve`,
    ),

  pendingKeypairs: () =>
    request<{ keypairs: PendingKeypair[] }>(
      "GET",
      "/v1/admin/keypairs/pending",
    ),

  expiringCerts: (within_days: number) =>
    request<{ certs: ExpiringCert[] }>(
      "GET",
      `/v1/admin/certs/expiring?within_days=${within_days}`,
    ),

  clearKeypair: (account_id: string) =>
    request<{ ok: boolean; already_clear: boolean }>(
      "POST",
      `/v1/admin/accounts/${account_id}/clear-keypair`,
    ),

  auditSearch: (params: {
    limit?: number;
    account_id?: string;
    action?: string;
  }) => {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    if (params.account_id) q.set("account_id", params.account_id);
    if (params.action) q.set("action", params.action);
    return request<{ rows: AuditSearchRow[]; chain_verified: boolean }>(
      "GET",
      `/v1/admin/audit?${q.toString()}`,
    );
  },

  accountsList: (params: { q?: string; limit?: number; offset?: number }) => {
    const qp = new URLSearchParams();
    if (params.q) qp.set("q", params.q);
    if (params.limit) qp.set("limit", String(params.limit));
    if (params.offset) qp.set("offset", String(params.offset));
    return request<{
      accounts: AccountSummary[];
      total: number;
    }>("GET", `/v1/admin/accounts?${qp.toString()}`);
  },
  accountDetail: (id: string) =>
    request<AccountDetail>("GET", `/v1/admin/accounts/${id}`),
  impersonate: (id: string) =>
    request<ImpersonateResult>("POST", `/v1/admin/impersonate/${id}`),
  resetPassword: (id: string) =>
    request<ResetPasswordResult>(
      "POST", `/v1/admin/accounts/${id}/reset-password`,
    ),
  signupsList: () =>
    request<{ signups: SignupReviewRow[] }>("GET", "/v1/admin/signups"),
  approveSignup: (id: string) =>
    request<{ account_id: string; email: string; signup_status: string }>(
      "POST", `/v1/admin/accounts/${id}/approve`,
    ),
  rejectSignup: (id: string, reason: string) =>
    request<{ account_id: string; email: string; signup_status: string }>(
      "POST", `/v1/admin/accounts/${id}/reject`, { reason },
    ),
  blockAccount: (id: string, reason: string) =>
    request<BlockResult>(
      "POST", `/v1/admin/accounts/${id}/block`, { reason },
    ),
  unblockAccount: (id: string) =>
    request<BlockResult>("POST", `/v1/admin/accounts/${id}/unblock`),
};

export interface SignupReviewRow {
  account_id: string;
  email: string;
  display_name: string;
  email_verified: boolean;
  created_at: string;
}

export interface ResetPasswordResult {
  account_id: string;
  email: string;
  temporary_password: string;
  sessions_revoked: number;
}

export interface AccountSummary {
  account_id: string;
  email: string;
  display_name: string;
  subscription_tier: string;
  is_operator: boolean;
  has_2fa: boolean;
  created_at: string;
  deleted_at: string | null;
  /** 'approved' | 'pending_review' | 'rejected' | 'blocked'. Drives the
   *  per-row action buttons in AccountsPanel. */
  signup_status: string;
}

export interface AccountDetail extends AccountSummary {
  phone_e164: string | null;
  counts: { clients: number; campaigns: number; runs: number };
}

export interface BlockResult {
  account_id: string;
  email: string;
  signup_status: string;
  sessions_revoked: number;
}

export interface ImpersonateResult {
  access_token: string;
  refresh_token: string;
  access_expires_in: number;
  refresh_expires_in: number;
  actor: { type: string; operator: string; accountant_id: string };
}
